import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Tabs, Form, Input, InputNumber, Button, Select, Switch,
  message, Card, Space, Upload, Image, Modal, Popconfirm, DatePicker, Tag,
} from 'antd';
import dayjs from 'dayjs';
import { apiClient } from 'Utils/api';
import {
  ArrowLeftOutlined, SaveOutlined, PlusOutlined, UploadOutlined,
  DeleteOutlined, EditOutlined, DragOutlined, RotateLeftOutlined, RotateRightOutlined, EyeOutlined, EyeInvisibleOutlined
} from '@ant-design/icons';
import { watchesAPI, languagesAPI } from '../../Utils/api';
import { taxonomyAPI } from '../../Utils/taxonomyApi';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import ImgCrop from 'antd-img-crop';
import { useLanguage } from '../../Context/LanguageContext';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './ProductDetailPage.css';
import { copyText } from '../../Utils/clipboard';
import { useIsMobile } from '../../Utils/useIsMobile';

const { TextArea } = Input;
const { Option } = Select;

interface Watch {
  id: string;
  slug: string;
  brand: string;
  model: string;
  referenceNumber?: string;
  sku?: string;
  priceCents: number;
  priceEur?: number;
  status: string;
  description?: string;
  yearOfProduction?: number;
  hasBox?: boolean;
  hasPapers?: boolean;
  taxScheme?: 'MARGIN_25A_USTG' | 'STANDARD_VAT_19';
  shortCode?: string | null;
  reservedUntil?: string | null;
  soldAt?: string | null;
}

interface Category {
  id: string;
  slug: string;
  name: string;
  isMultiSelect: boolean;
  options: TaxonomyOption[];
}

interface TaxonomyOption {
  id: string;
  slug: string;
  value: string;
  categoryId: string;
}

interface WatchImage {
  id: string;
  originalUrl: string;
  thumbnailUrl?: string;
  displayOrder: number;
  altText?: string;
}

// Sortable Image Item Component
interface SortableImageItemProps {
  image: WatchImage;
  watchId: string;
  onDelete: (id: string) => void;
  onEditAltText: (image: WatchImage) => void;
  onRotated: () => void;
}

const FE_URL = process.env.REACT_APP_FE_URL || 'https://www.mr-luxury-watches.com';


const SortableImageItem: React.FC<SortableImageItemProps> = ({ image, watchId, onDelete, onEditAltText, onRotated }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const API_URL = process.env.REACT_APP_API_URL || '';
  const isMobile = useIsMobile();
  const [bust, setBust] = React.useState<number>(Date.now());
  const [rotating, setRotating] = React.useState(false);

  const rotate = async (direction: 'left' | 'right') => {
    setRotating(true);
    try {
      await apiClient.post(`${API_URL}/api/admin/watches/${watchId}/images/${image.id}/rotate`, { direction });
      setBust(Date.now());
      onRotated();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Drehen fehlgeschlagen');
    } finally {
      setRotating(false);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="sortable-image-item"
    >
      <Card
        size="small"
        style={{ width: isMobile ? '100%' : 220, marginBottom: isMobile ? 0 : 16 }}
        cover={
          <div style={{ position: 'relative' }}>
            <Image
              src={`${API_URL}${image.thumbnailUrl || image.originalUrl}?v=${bust}`}
              alt={image.altText || 'Produktbild'}
              preview={{
                src: `${API_URL}${image.originalUrl}?v=${bust}`,
              }}
              style={{ width: '100%', height: isMobile ? 'auto' : 220, aspectRatio: isMobile ? '1' : undefined, objectFit: 'cover' }}
            />
            <div
              {...listeners}
              style={{
                position: 'absolute',
                top: 8,
                left: 8,
                background: 'rgba(0,0,0,0.6)',
                color: 'white',
                padding: isMobile ? '8px 12px' : '4px 8px',
                borderRadius: 4,
                cursor: 'move',
                // Lets the pointer sensor drag on touch screens; the rest of the card still scrolls the page
                touchAction: 'none',
              }}
            >
              <DragOutlined />{!isMobile && ' Sortieren'}
            </div>
            <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 4 }}>
              <Button
                size="small"
                icon={<RotateLeftOutlined />}
                loading={rotating}
                onClick={() => rotate('left')}
                title="90° nach links drehen"
                style={{ background: 'rgba(255,255,255,0.9)' }}
              />
              <Button
                size="small"
                icon={<RotateRightOutlined />}
                loading={rotating}
                onClick={() => rotate('right')}
                title="90° nach rechts drehen"
                style={{ background: 'rgba(255,255,255,0.9)' }}
              />
            </div>
          </div>
        }
        actions={[
          <Button
            key="edit"
            type="text"
            icon={<EditOutlined />}
            onClick={() => onEditAltText(image)}
          >
            Alt-Text
          </Button>,
          <Popconfirm
            key="delete"
            title="Bild löschen?"
            description="Möchten Sie dieses Bild wirklich löschen?"
            onConfirm={() => onDelete(image.id)}
            okText="Ja"
            cancelText="Nein"
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              Löschen
            </Button>
          </Popconfirm>,
        ]}
      >
        <Card.Meta
          description={
            <div style={{ fontSize: 12, color: '#666', wordBreak: 'break-word' }}>
              {image.altText || <em>Kein Alt-Text</em>}
            </div>
          }
        />
      </Card>
    </div>
  );
};

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Entwurf', AVAILABLE: 'Verfügbar', RESERVED: 'Reserviert', SOLD: 'Verkauft', ARCHIVED: 'Archiviert',
};
const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default', AVAILABLE: 'green', RESERVED: 'gold', SOLD: 'red', ARCHIVED: 'default',
};

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [generalForm] = Form.useForm();

  const [watch, setWatch] = useState<Watch | null>(null);
  const [saleOrder, setSaleOrder] = useState<any | null>(null);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [reserveDate, setReserveDate] = useState<dayjs.Dayjs | null>(null);
  const [reserving, setReserving] = useState(false);
  const isMobile = useIsMobile();
  const [soldOpen, setSoldOpen] = useState(false);
  const [soldDate, setSoldDate] = useState<dayjs.Dayjs>(dayjs());
  const [markingSold, setMarkingSold] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  // Use global language context
  const { currentLanguage, multiLanguageData, setMultiLanguageData } = useLanguage();

  // Categories & Taxonomy
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string[]>>({});
  const [taxonomyLoading, setTaxonomyLoading] = useState(false);

  // Images
  const [images, setImages] = useState<WatchImage[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [altTextModalVisible, setAltTextModalVisible] = useState(false);
  const [editingImage, setEditingImage] = useState<WatchImage | null>(null);
  const [altTextForm] = Form.useForm();

  // Drag & Drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const isNewWatch = id === 'new';

  useEffect(() => {
    if (!isNewWatch && id) {
      fetchWatch();
      fetchTaxonomy();
      fetchImages();
      fetchTranslations();
    }
    fetchCategories();
  }, [id]);

  // Load translations when language changes
  useEffect(() => {
    if (!isNewWatch && id && watch) {
      // Existing watch: Load from fetched data
      loadFormDataForLanguage();
    } else if (isNewWatch) {
      // New watch: Load from multiLanguageData
      loadFormDataForNewWatch();
    }
  }, [currentLanguage, watch, multiLanguageData]);

  const fetchTranslations = async (watchId?: string) => {
    const targetId = watchId || id;
    if (!targetId) return;

    try {
      const response = await languagesAPI.getTranslations('watch', targetId);
      const translationsData = response.data.translations || {};
      setMultiLanguageData(translationsData);
    } catch (error) {
      console.error('Error fetching translations:', error);
    }
  };

  const loadFormDataForLanguage = () => {
    if (currentLanguage === 'de') {
      // Load German (master) data
      generalForm.setFieldsValue({
        brand: watch?.brand || '',
        model: watch?.model || '',
        referenceNumber: watch?.referenceNumber || '',
        slug: watch?.slug || '',
        priceEur: watch?.priceEur || '',
        description: watch?.description || '',
        status: watch?.status || 'AVAILABLE',
        hasBox: watch?.hasBox || false,
        hasPapers: watch?.hasPapers || false,
        taxScheme: watch?.taxScheme || 'MARGIN_25A_USTG',
      });
    } else {
      // Load translation data if exists, keep German as placeholder
      const langData = multiLanguageData[currentLanguage] || {};
      generalForm.setFieldsValue({
        brand: watch?.brand || '',
        model: langData.model || '',
        referenceNumber: watch?.referenceNumber || '',
        slug: watch?.slug || '',
        priceEur: watch?.priceEur || '',
        description: langData.description || '',
        status: watch?.status || 'AVAILABLE',
        hasBox: watch?.hasBox || false,
        hasPapers: watch?.hasPapers || false,
        taxScheme: watch?.taxScheme || 'MARGIN_25A_USTG',
      });
    }
  };

  const loadFormDataForNewWatch = () => {
    // For new watches, load data from multiLanguageData
    if (currentLanguage === 'de') {
      const deData = multiLanguageData['de'] || {};
      generalForm.setFieldsValue({
        brand: deData.brand || '',
        model: deData.model || '',
        referenceNumber: deData.referenceNumber || '',
        slug: deData.slug || '',
        priceEur: deData.priceEur || '',
        description: deData.description || '',
        status: deData.status || 'AVAILABLE',
        hasBox: deData.hasBox || false,
        hasPapers: deData.hasPapers || false,
        yearOfProduction: deData.yearOfProduction,
        taxScheme: deData.taxScheme || 'MARGIN_25A_USTG',
      });
    } else {
      const langData = multiLanguageData[currentLanguage] || {};
      const deData = multiLanguageData['de'] || {};
      generalForm.setFieldsValue({
        brand: deData.brand || '',
        model: langData.model || '',
        referenceNumber: deData.referenceNumber || '',
        slug: deData.slug || '',
        priceEur: deData.priceEur || '',
        description: langData.description || '',
        status: deData.status || 'AVAILABLE',
        hasBox: deData.hasBox || false,
        hasPapers: deData.hasPapers || false,
        yearOfProduction: deData.yearOfProduction,
        taxScheme: deData.taxScheme || 'MARGIN_25A_USTG',
      });
    }
  };

  // Track form changes and store in multiLanguageData
  const handleFormChange = (changedValues: any, allValues: any) => {
    if (isNewWatch) {
      // For new watches, store ALL language data (including DE)
      const translationFields = ['model', 'description'];
      const updatedLangData = { ...multiLanguageData[currentLanguage] };

      translationFields.forEach(field => {
        if (changedValues[field] !== undefined) {
          updatedLangData[field] = changedValues[field];
        }
      });

      // Also store language-independent fields in DE data
      if (currentLanguage === 'de') {
        ['brand', 'referenceNumber', 'sku', 'slug', 'priceEur', 'status', 'hasBox', 'hasPapers', 'yearOfProduction', 'taxScheme'].forEach(field => {
          if (changedValues[field] !== undefined) {
            updatedLangData[field] = changedValues[field];
          }
        });
      }

      setMultiLanguageData({
        ...multiLanguageData,
        [currentLanguage]: updatedLangData,
      });
    } else if (currentLanguage !== 'de') {
      // For existing watches, only store non-German translations
      const translationFields = ['model', 'description'];
      const updatedLangData = { ...multiLanguageData[currentLanguage] };

      translationFields.forEach(field => {
        if (changedValues[field] !== undefined) {
          updatedLangData[field] = changedValues[field];
        }
      });

      setMultiLanguageData({
        ...multiLanguageData,
        [currentLanguage]: updatedLangData,
      });
    }
  };

  const fetchWatch = async () => {
    if (!id || isNewWatch) return;
    setLoading(true);
    try {
      const response = await watchesAPI.getWatch(id);
      const watchData = response.data.watch || response.data;

      // Convert priceCents to priceEur
      const priceEur = watchData.priceCents ? watchData.priceCents / 100 : 0;

      setWatch({ ...watchData, priceEur });
      generalForm.setFieldsValue({
        ...watchData,
        priceEur,
      });

      // Fetch associated sale/order if any
      if (watchData.status === 'SOLD') {
        try {
          const saleRes = await watchesAPI.getSale(id);
          setSaleOrder(saleRes.data?.order || null);
        } catch (e) {
          setSaleOrder(null);
        }
      } else {
        setSaleOrder(null);
      }
    } catch (error) {
      message.error('Fehler beim Laden des Produkts');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await taxonomyAPI.getAllCategories();
      const cats = response.data.categories || [];

      // Fetch options for each category
      const categoriesWithOptions = await Promise.all(
        cats.map(async (cat: any) => {
          const optionsResponse = await taxonomyAPI.getCategoryOptions(cat.id);
          return {
            ...cat,
            options: optionsResponse.data.options || [],
          };
        })
      );

      setCategories(categoriesWithOptions.filter((c: any) => c.isActive));
    } catch (error) {
      message.error('Fehler beim Laden der Kategorien');
    }
  };

  const fetchTaxonomy = async () => {
    if (!id || isNewWatch) return;
    setTaxonomyLoading(true);
    try {
      const response = await watchesAPI.getTaxonomy(id);
      const taxonomyData = response.data.taxonomy || [];

      console.log('Loaded taxonomy data:', taxonomyData); // Debug

      // Group by categoryId
      const optionsByCategory: Record<string, string[]> = {};
      taxonomyData.forEach((item: any) => {
        if (item.option && item.option.category) {
          const catId = item.option.category.id;
          if (!optionsByCategory[catId]) {
            optionsByCategory[catId] = [];
          }
          optionsByCategory[catId].push(item.optionId);
        }
      });

      console.log('Mapped options by category:', optionsByCategory); // Debug
      setSelectedOptions(optionsByCategory);
    } catch (error) {
      console.error('Error loading taxonomy:', error);
      message.error('Fehler beim Laden der Filter');
    } finally {
      setTaxonomyLoading(false);
    }
  };

  const fetchImages = async () => {
    if (!id || isNewWatch) return;
    setImagesLoading(true);
    try {
      const response = await watchesAPI.getImages(id);
      const fetchedImages = response.data.images || [];
      // Sort by displayOrder to ensure correct order
      const sortedImages = fetchedImages.sort((a: WatchImage, b: WatchImage) =>
        a.displayOrder - b.displayOrder
      );
      setImages(sortedImages);
    } catch (error) {
      message.error('Fehler beim Laden der Bilder');
    } finally {
      setImagesLoading(false);
    }
  };

  const handleImageUpload = async () => {
    if (!id || fileList.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();

      // Prepare additional metadata
      const additionalData: any = {
        _additionalImageData: {},
      };

      fileList.forEach((file, index) => {
        if (file.originFileObj) {
          formData.append('images', file.originFileObj);
          additionalData._additionalImageData[file.originFileObj.name] = {
            name: file.name,
            mimetype: file.type,
            mimeType: file.type,
            sorting: images.length + index,
            size: file.size,
            aspect: 1,
            altText: '',
          };
        }
      });

      formData.append('data', JSON.stringify(additionalData));

      await watchesAPI.uploadImages(id, formData);
      message.success(`${fileList.length} Bild(er) erfolgreich hochgeladen`);
      setFileList([]);
      fetchImages();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Fehler beim Hochladen der Bilder');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId: string) => {
    if (!id) return;
    try {
      await watchesAPI.deleteImage(id, imageId);
      message.success('Bild erfolgreich gelöscht');
      fetchImages();
    } catch (error) {
      message.error('Fehler beim Löschen des Bildes');
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = images.findIndex((img) => img.id === active.id);
    const newIndex = images.findIndex((img) => img.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newImages = arrayMove(images, oldIndex, newIndex);

    // Update display order
    const updatedImages = newImages.map((img, index) => ({
      ...img,
      displayOrder: index,
    }));

    setImages(updatedImages);

    // Save to backend
    try {
      const imageOrders = updatedImages.map((img) => ({
        id: img.id,
        displayOrder: img.displayOrder,
      }));
      await watchesAPI.reorderImages(id!, imageOrders);
      message.success('Reihenfolge erfolgreich gespeichert');
    } catch (error) {
      message.error('Fehler beim Speichern der Reihenfolge');
      fetchImages(); // Reload on error
    }
  };

  const handleEditAltText = (image: WatchImage) => {
    setEditingImage(image);
    altTextForm.setFieldsValue({ altText: image.altText || '' });
    setAltTextModalVisible(true);
  };

  const handleSaveAltText = async () => {
    if (!id || !editingImage) return;

    try {
      const values = await altTextForm.validateFields();
      await watchesAPI.updateImage(id, editingImage.id, { altText: values.altText });
      message.success('Alt-Text erfolgreich gespeichert');
      setAltTextModalVisible(false);
      setEditingImage(null);
      altTextForm.resetFields();
      fetchImages();
    } catch (error) {
      message.error('Fehler beim Speichern des Alt-Texts');
    }
  };

  const handleSaveGeneral = async (values: any) => {
    setLoading(true);
    try {
      // CRITICAL: Separate German base data from translations
      const payload: any = {};

      // If user is currently editing in non-German language
      let translationsToSave = { ...multiLanguageData };

      if (isNewWatch) {
        // NEW WATCH: Use multiLanguageData for German base
        const deData = multiLanguageData['de'] || {};
        payload.brand = deData.brand || values.brand || '';
        payload.model = deData.model || values.model || '';
        payload.description = deData.description || values.description || '';
        payload.slug = values.slug || deData.slug;
        payload.referenceNumber = values.referenceNumber || deData.referenceNumber;
        payload.sku = values.sku || deData.sku;
        payload.priceEur = values.priceEur !== undefined ? values.priceEur : deData.priceEur;
        payload.status = values.status || deData.status || 'AVAILABLE';
        payload.yearOfProduction = values.yearOfProduction || deData.yearOfProduction;
        payload.hasBox = values.hasBox !== undefined ? values.hasBox : (deData.hasBox || false);
        payload.hasPapers = values.hasPapers !== undefined ? values.hasPapers : (deData.hasPapers || false);
        payload.taxScheme = values.taxScheme || deData.taxScheme || 'MARGIN_25A_USTG';

        // Store current form data if in non-German mode
        if (currentLanguage !== 'de') {
          const currentLangData: any = {};
          if (values.model && values.model.trim()) {
            currentLangData.model = values.model;
          }
          if (values.description && values.description.trim()) {
            currentLangData.description = values.description;
          }

          if (Object.keys(currentLangData).length > 0) {
            translationsToSave = {
              ...translationsToSave,
              [currentLanguage]: currentLangData,
            };
          }
        }
      } else {
        // EXISTING WATCH
        if (currentLanguage !== 'de') {
          // User is editing in non-German mode
          const currentLangData: any = {};
          if (values.model && values.model.trim()) {
            currentLangData.model = values.model;
          }
          if (values.description && values.description.trim()) {
            currentLangData.description = values.description;
          }

          if (Object.keys(currentLangData).length > 0) {
            translationsToSave = {
              ...translationsToSave,
              [currentLanguage]: currentLangData,
            };
          }

          // Use original watch data for German base (prevent overwriting German with EN)
          payload.brand = watch?.brand || values.brand;
          payload.model = watch?.model || '';
          payload.description = watch?.description || '';
        } else {
          // User is in German mode - use form values for base data
          payload.brand = values.brand;
          payload.model = values.model;
          payload.description = values.description;
        }

        // Always include these fields from form (language-independent)
        payload.slug = values.slug;
        payload.referenceNumber = values.referenceNumber;
        payload.sku = values.sku;
        payload.priceEur = values.priceEur;
        payload.yearOfProduction = values.yearOfProduction;
        payload.hasBox = values.hasBox;
        payload.hasPapers = values.hasPapers;
        payload.taxScheme = values.taxScheme;
      }

      // Add translations if we have any non-German data
      const hasTranslations = Object.keys(translationsToSave).some(
        lang => lang !== 'de' && translationsToSave[lang] && Object.keys(translationsToSave[lang]).length > 0
      );

      if (hasTranslations) {
        payload.translations = {};
        Object.keys(translationsToSave).forEach(lang => {
          if (lang !== 'de' && translationsToSave[lang]) {
            const langData = translationsToSave[lang];
            const hasData = Object.values(langData).some(val => val && String(val).trim());
            if (hasData) {
              payload.translations[lang] = langData;
            }
          }
        });
      }

      console.log('Payload being sent:', payload); // Debug

      if (isNewWatch) {
        const response = await watchesAPI.createWatch(payload);
        message.success('Produkt erfolgreich erstellt');
        const newWatch = response.data.watch;

        // Update URL to new watch ID
        navigate(`/products/${newWatch.id}`, { replace: true });

        // CRITICAL: Load the newly created watch and its translations
        setWatch(newWatch);

        // Fetch translations with the new watch ID
        await fetchTranslations(newWatch.id);

        // Load form data for current language
        if (currentLanguage === 'de') {
          generalForm.setFieldsValue({
            brand: newWatch.brand || '',
            model: newWatch.model || '',
            referenceNumber: newWatch.referenceNumber || '',
            slug: newWatch.slug || '',
            priceEur: newWatch.priceEur || '',
            description: newWatch.description || '',
            status: newWatch.status || 'AVAILABLE',
            hasBox: newWatch.hasBox || false,
            hasPapers: newWatch.hasPapers || false,
            yearOfProduction: newWatch.yearOfProduction,
          });
        } else {
          // If we're in non-DE mode, show the translation
          const langData = translationsToSave[currentLanguage] || {};
          generalForm.setFieldsValue({
            brand: newWatch.brand || '',
            model: langData.model || '',
            referenceNumber: newWatch.referenceNumber || '',
            slug: newWatch.slug || '',
            priceEur: newWatch.priceEur || '',
            description: langData.description || '',
            status: newWatch.status || 'AVAILABLE',
            hasBox: newWatch.hasBox || false,
            hasPapers: newWatch.hasPapers || false,
            yearOfProduction: newWatch.yearOfProduction,
          });
        }
      } else if (id) {
        await watchesAPI.updateWatch(id, payload);
        message.success('Produkt erfolgreich gespeichert');

        // Update multiLanguageData state with saved translations
        setMultiLanguageData(translationsToSave);

        fetchWatch();
        fetchTranslations();
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Fehler beim Speichern des Produkts');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTaxonomy = async () => {
    if (!id || isNewWatch) return;
    setTaxonomyLoading(true);
    try {
      // Flatten all selected options
      const allOptionIds = Object.values(selectedOptions).flat();
      await watchesAPI.updateTaxonomy(id, allOptionIds);
      message.success('Filter erfolgreich gespeichert');
    } catch (error) {
      message.error('Fehler beim Speichern der Filter');
    } finally {
      setTaxonomyLoading(false);
    }
  };

  const handleCategoryChange = (categoryId: string, value: string | string[]) => {
    setSelectedOptions(prev => ({
      ...prev,
      [categoryId]: Array.isArray(value) ? value : [value],
    }));
  };

  const handleTabChange = (key: string) => {
    if (key === 'back') {
      navigate('/products');
    } else {
      setActiveTab(key);
    }
  };

  const tabItems = [
    {
      key: 'back',
      label: <span><ArrowLeftOutlined /> Zurück zur Übersicht</span>,
      children: null,
    },
    {
      key: 'general',
      label: 'Allgemeine Daten',
      children: (
        <Card>
          <Form
            form={generalForm}
            layout="vertical"
            onFinish={handleSaveGeneral}
            onValuesChange={handleFormChange}
            style={{ maxWidth: 800 }}
            initialValues={{
              status: 'AVAILABLE',
              hasBox: false,
              hasPapers: false,
            }}
          >
            <Form.Item
              name="brand"
              label="Marke"
              rules={[{ required: true, message: 'Bitte Marke eingeben' }]}
            >
              <Input placeholder="z.B. Rolex" />
            </Form.Item>

            <Form.Item
              name="model"
              label={`Modell${currentLanguage !== 'de' ? ` (${currentLanguage.toUpperCase()})` : ''}`}
              rules={[{ required: true, message: 'Bitte Modell eingeben' }]}
              tooltip={currentLanguage !== 'de' ? 'Deutsche Übersetzung wird als Placeholder verwendet' : undefined}
            >
              <Input
                placeholder={
                  currentLanguage === 'de'
                    ? 'z.B. Submariner'
                    : watch?.model || 'Deutsche Übersetzung'
                }
              />
            </Form.Item>

            <Form.Item
              name="slug"
              label="Slug (optional)"
              tooltip="Wird automatisch aus Marke + Modell generiert"
            >
              <Input placeholder="z.B. rolex-submariner (optional)" />
            </Form.Item>

            <Form.Item
              name="referenceNumber"
              label="Referenznummer"
            >
              <Input placeholder="z.B. 126610LN" />
            </Form.Item>

            <Form.Item
              name="sku"
              label="SKU"
              tooltip="Eindeutige Lagerverwaltungs-Nummer pro Uhr. Wird dem Support bei einer Uhren-Anfrage automatisch übergeben."
            >
              <Input placeholder="z.B. MRLW-0001" />
            </Form.Item>

            <Form.Item
              name="priceEur"
              label="Preis (EUR)"
              rules={[{ required: true, message: 'Bitte Preis eingeben' }]}
            >
              <InputNumber
                min={0}
                step={100}
                style={{ width: '100%' }}
                prefix="€"
                placeholder="z.B. 15000"
                inputMode="decimal"
              />
            </Form.Item>

            <Form.Item label="Status" extra={isNewWatch ? undefined : 'Status wird über die Aktionen oben neben dem Produkttitel geändert.'}>
              <Tag color={STATUS_COLORS[watch?.status || 'AVAILABLE']} data-testid="admin-pdp-status-info">
                {STATUS_LABELS[watch?.status || 'AVAILABLE']}
              </Tag>
            </Form.Item>

            <Form.Item
              name="yearOfProduction"
              label="Produktionsjahr"
            >
              <InputNumber min={1900} max={new Date().getFullYear()} style={{ width: '100%' }} inputMode="numeric" />
            </Form.Item>

            <Form.Item
              name="taxScheme"
              label="Besteuerung"
              tooltip="Für Gebrauchtuhren typischerweise Differenzbesteuerung nach §25a UStG. Regelbesteuerung 19% nur bei Neuware oder Händler-zu-Händler."
            >
              <Select>
                <Option value="MARGIN_25A_USTG">Differenzbesteuerung gem. §25a UStG</Option>
                <Option value="STANDARD_VAT_19">Regelbesteuerung (inkl. 19% MwSt.)</Option>
              </Select>
            </Form.Item>

            <Space size="large">
              <Form.Item
                name="hasBox"
                label="Box vorhanden"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="hasPapers"
                label="Papiere vorhanden"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Space>

            <Form.Item
              name="description"
              label={`Beschreibung${currentLanguage !== 'de' ? ` (${currentLanguage.toUpperCase()})` : ''}`}
              tooltip={currentLanguage !== 'de' ? 'Deutsche Beschreibung wird als Placeholder verwendet' : undefined}
            >
              <Input.TextArea
                rows={6}
                placeholder={
                  currentLanguage === 'de'
                    ? 'Produktbeschreibung...'
                    : watch?.description || 'Deutsche Beschreibung'
                }
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                >
                  Speichern
                </Button>
                <Button onClick={() => navigate('/products')}>
                  Abbrechen
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      ),
    },
    {
      key: 'taxonomy',
      label: 'Filter / Kategorien',
      disabled: isNewWatch,
      children: isNewWatch ? (
        <Card>
          <p>Bitte speichern Sie zuerst die allgemeinen Daten.</p>
        </Card>
      ) : (
        <Card>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {categories.map((category) => (
              <div key={category.id}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  {category.name} {category.isMultiSelect && <span style={{ color: '#999', fontWeight: 'normal' }}>(Mehrfachauswahl)</span>}
                </label>
                <Select
                  mode={category.isMultiSelect ? 'multiple' : undefined}
                  showSearch
                  style={{ width: '100%', maxWidth: 600 }}
                  placeholder={`${category.name} auswählen${category.isMultiSelect ? ' (mehrere möglich)' : ''}`}
                  value={
                    category.isMultiSelect
                      ? (selectedOptions[category.id] || [])
                      : (selectedOptions[category.id]?.[0] || undefined)
                  }
                  onChange={(value) => handleCategoryChange(category.id, value)}
                  allowClear
                  optionFilterProp="children"
                  maxTagCount="responsive"
                >
                  {category.options.map((option) => (
                    <Option key={option.id} value={option.id}>
                      {option.value}
                    </Option>
                  ))}
                </Select>
              </div>
            ))}

            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSaveTaxonomy}
              loading={taxonomyLoading}
              style={{ marginTop: 16 }}
            >
              Filter speichern
            </Button>
          </Space>
        </Card>
      ),
    },
    {
      key: 'images',
      label: 'Bilder',
      disabled: isNewWatch,
      children: isNewWatch ? (
        <Card>
          <p>Bitte speichern Sie zuerst die allgemeinen Daten.</p>
        </Card>
      ) : (
        <Card>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* Upload Section */}
            <div>
              <h3>Bilder hochladen</h3>
              <p style={{ color: '#666', marginBottom: 16 }}>
                Mehrere Bilder gleichzeitig auswählen möglich. Unterstützte Formate: JPG, PNG, WEBP. Drehen ist im Nachhinein über die Buttons unter jedem Bild möglich.
              </p>
              <Upload
                listType="picture-card"
                fileList={fileList}
                onChange={({ fileList: newFileList }) => setFileList(newFileList)}
                beforeUpload={() => false}
                multiple
                accept="image/jpeg,image/jpg,image/png,image/webp"
                maxCount={20}
              >
                {fileList.length < 20 && (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Hochladen</div>
                  </div>
                )}
              </Upload>

              {fileList.length > 0 && (
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={handleImageUpload}
                  loading={uploading}
                  style={{ marginTop: 16 }}
                >
                  {fileList.length} Bild(er) hochladen
                </Button>
              )}
            </div>

            {/* Images Gallery with Drag & Drop */}
            {images.length > 0 && (
              <div>
                <h3>Vorhandene Bilder ({images.length})</h3>
                <p style={{ color: '#666', marginBottom: 16 }}>
                  Ziehen Sie die Bilder, um die Reihenfolge zu ändern
                </p>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={images.map((img) => img.id)}
                    strategy={rectSortingStrategy}
                  >
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                      {images.map((image) => (
                        <SortableImageItem
                          key={image.id}
                          image={image}
                          watchId={id || ''}
                          onDelete={handleDeleteImage}
                          onEditAltText={handleEditAltText}
                          onRotated={fetchImages}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            )}

            {images.length === 0 && !imagesLoading && (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                <p>Noch keine Bilder vorhanden</p>
              </div>
            )}
          </Space>

          {/* Alt-Text Modal */}
          <Modal
            title="Alt-Text bearbeiten"
            open={altTextModalVisible}
            onOk={handleSaveAltText}
            onCancel={() => {
              setAltTextModalVisible(false);
              setEditingImage(null);
              altTextForm.resetFields();
            }}
            okText="Speichern"
            cancelText="Abbrechen"
          >
            <Form form={altTextForm} layout="vertical">
              <Form.Item
                name="altText"
                label="Alt-Text (für Barrierefreiheit & SEO)"
                rules={[{ max: 255, message: 'Maximal 255 Zeichen' }]}
              >
                <Input.TextArea
                  rows={3}
                  placeholder="z.B. Rolex Submariner Date Edelstahl schwarz"
                  maxLength={255}
                  showCount
                />
              </Form.Item>
            </Form>
          </Modal>
        </Card>
      ),
    },
  ];

  const changeStatus = async (action: 'unarchive' | 'unsell') => {
    try {
      const res = await apiClient.post(`/api/admin/watches/${id}/status`, { action });
      if (res.data?.status === 'error') {
        message.error(res.data.message);
        return;
      }
      message.success(res.data?.message || 'Status geändert');
      fetchWatch();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Aktion fehlgeschlagen');
    }
  };

  return (
    <div className="admin-pdp">
      <div className="admin-pdp-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
        <Space align="center" wrap>
          <h1 style={{ margin: 0 }}>
            {isNewWatch ? 'Neues Produkt' : watch ? `${watch.brand} ${watch.model}` : 'Produkt bearbeiten'}
          </h1>
          {!isNewWatch && watch && (
            <Tag color={STATUS_COLORS[watch.status]} style={{ fontSize: 13 }} data-testid="admin-pdp-status-tag">
              {STATUS_LABELS[watch.status] || watch.status}
            </Tag>
          )}
          {!isNewWatch && watch?.status === 'RESERVED' && (
            <span style={{ color: '#888', fontSize: 13 }}>
              {watch.reservedUntil ? `bis ${dayjs(watch.reservedUntil).format('DD.MM.YYYY HH:mm')}` : 'ohne Ablauffrist'}
            </span>
          )}
        </Space>
        {!isNewWatch && watch && (
          <Space wrap className="admin-pdp-actions">
            {watch.status === 'DRAFT' && (
              <Popconfirm
                title="Produkt veröffentlichen"
                description="Das Produkt wird damit im Shop für Kunden sichtbar."
                okText="Veröffentlichen"
                cancelText="Abbrechen"
                onConfirm={async () => {
                  try {
                    await apiClient.post(`/api/admin/watches/${id}/publish`, { publish: true });
                    message.success('Produkt veröffentlicht');
                    fetchWatch();
                  } catch (e: any) {
                    message.error(e?.response?.data?.message || 'Veröffentlichen fehlgeschlagen');
                  }
                }}
              >
                <Button type="primary" icon={<EyeOutlined />} data-testid="admin-pdp-publish-btn">
                  Veröffentlichen
                </Button>
              </Popconfirm>
            )}
            {watch.status === 'AVAILABLE' && (
              <Popconfirm
                title="Produkt verstecken"
                description="Das Produkt wird wieder als Entwurf gespeichert und ist nicht mehr im Shop sichtbar."
                okText="Verstecken"
                cancelText="Abbrechen"
                onConfirm={async () => {
                  try {
                    await apiClient.post(`/api/admin/watches/${id}/publish`, { publish: false });
                    message.success('Produkt verborgen');
                    fetchWatch();
                  } catch (e: any) {
                    message.error(e?.response?.data?.message || 'Aktion fehlgeschlagen');
                  }
                }}
              >
                <Button icon={<EyeInvisibleOutlined />} data-testid="admin-pdp-unpublish-btn">
                  Verstecken
                </Button>
              </Popconfirm>
            )}
            {watch.status === 'AVAILABLE' || watch.status === 'RESERVED' ? (
              <Button
                type="primary"
                onClick={() => {
                  setReserveDate(watch.reservedUntil ? dayjs(watch.reservedUntil) : watch.status === 'RESERVED' ? null : dayjs().add(7, 'day'));
                  setReserveOpen(true);
                }}
                data-testid="admin-pdp-reserve-btn"
              >
                {watch.status === 'RESERVED' ? 'Reservierung anpassen' : 'Reservieren'}
              </Button>
            ) : null}
            {watch.status === 'RESERVED' && (
              <Popconfirm
                title="Reservierung aufheben?"
                description="Die Uhr wird sofort wieder verfügbar."
                okText="Ja"
                cancelText="Abbrechen"
                onConfirm={async () => {
                  setReserving(true);
                  try {
                    await apiClient.post(`/api/admin/watches/${id}/reserve`, { reservedUntil: null });
                    message.success('Reservierung aufgehoben');
                    fetchWatch();
                  } catch (e: any) {
                    message.error(e.response?.data?.message || 'Fehler');
                  } finally {
                    setReserving(false);
                  }
                }}
              >
                <Button loading={reserving} data-testid="admin-pdp-clear-reservation">
                  Reservierung aufheben
                </Button>
              </Popconfirm>
            )}
            {(watch.status === 'AVAILABLE' || watch.status === 'RESERVED') && (
              <Button
                color="green"
                variant="solid"
                onClick={() => {
                  setSoldDate(dayjs());
                  setSoldOpen(true);
                }}
                data-testid="admin-pdp-mark-sold-btn"
              >
                Als verkauft markieren
              </Button>
            )}
            {watch.status === 'SOLD' && saleOrder && saleOrder.status !== 'CANCELLED' && (
              <Button onClick={() => navigate(`/orders/${saleOrder.id}`)} data-testid="admin-pdp-cancel-order-btn">
                Freigeben über Stornierung der Bestellung
              </Button>
            )}
            {watch.status === 'SOLD' && (!saleOrder || saleOrder.status === 'CANCELLED') && (
              <Popconfirm
                title="Verkauf zurücksetzen?"
                description="Verkaufsdatum und Verkaufspreis werden entfernt, die Uhr ist wieder verfügbar."
                okText="Zurücksetzen"
                cancelText="Abbrechen"
                onConfirm={() => changeStatus('unsell')}
              >
                <Button data-testid="admin-pdp-unsell-btn">Verkauf zurücksetzen</Button>
              </Popconfirm>
            )}
            {watch.status === 'ARCHIVED' && (
              <Popconfirm
                title="Archivierung aufheben?"
                description="Das Produkt wird als Entwurf wiederhergestellt und kann danach veröffentlicht werden."
                okText="Aufheben"
                cancelText="Abbrechen"
                onConfirm={() => changeStatus('unarchive')}
              >
                <Button data-testid="admin-pdp-unarchive-btn">Archivierung aufheben</Button>
              </Popconfirm>
            )}
            <Popconfirm
              title="Produkt löschen"
              description={`Soll "${watch.brand} ${watch.model}" wirklich gelöscht werden? Dieser Vorgang kann nicht rückgängig gemacht werden.`}
              okText="Ja, löschen"
              cancelText="Abbrechen"
              okButtonProps={{ danger: true }}
              onConfirm={async () => {
                try {
                  await apiClient.delete(`/api/admin/watches/${id}`);
                  message.success('Produkt gelöscht');
                  navigate('/products');
                } catch (e: any) {
                  message.error(e?.response?.data?.message || 'Löschen fehlgeschlagen');
                }
              }}
            >
              <Button danger icon={<DeleteOutlined />} data-testid="admin-pdp-delete-btn">
                Produkt löschen
              </Button>
            </Popconfirm>
          </Space>
        )}
      </div>

      {!isNewWatch && watch?.shortCode && (
        <div
          data-testid="admin-pdp-shortlink"
          className="admin-pdp-shortlink"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 12px',
            marginBottom: 16,
            background: '#fcfaf4',
            border: '1px solid #e6e3da',
            borderRadius: 4,
            fontSize: 13,
          }}
        >
          <span style={{ color: '#888' }}>Short-Link:</span>
          <a
            href={`${FE_URL}/s/${watch.shortCode}`}
            target="_blank"
            rel="noreferrer"
            style={{ fontFamily: 'monospace', color: '#b8964a' }}
          >
            {`${FE_URL}/s/${watch.shortCode}`}
          </a>
          <Button
            size="small"
            type="text"
            onClick={async () => {
              if (await copyText(`${FE_URL}/s/${watch.shortCode}`)) {
                message.success('Link kopiert');
              }
            }}
            data-testid="admin-pdp-copy-shortlink"
          >
            Kopieren
          </Button>
        </div>
      )}

      {!isNewWatch && watch?.status === 'SOLD' && (
        <div
          data-testid="admin-pdp-sale-box"
          className="admin-pdp-sale-box"
          style={{
            display: 'block',
            padding: '12px 16px',
            marginBottom: 24,
            background: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: 6,
            fontSize: 14,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <strong style={{ color: '#d46b08' }}>Diese Uhr wurde verkauft.</strong>
              {watch.soldAt && (
                <span style={{ marginLeft: 12, color: '#555' }}>am {dayjs(watch.soldAt).format('DD.MM.YYYY')}</span>
              )}
              {saleOrder ? (
                <span style={{ marginLeft: 12, color: '#555' }}>
                  Bestellung <strong>{saleOrder.orderNumber}</strong> ·{' '}
                  {saleOrder.customerFirstName} {saleOrder.customerLastName} ·{' '}
                  {saleOrder.customerEmail}
                </span>
              ) : (
                <span style={{ marginLeft: 12, color: '#999' }}>
                  (Direktverkauf ohne Online-Bestellung)
                </span>
              )}
            </div>
            {saleOrder && (
              <Button
                type="primary"
                size="small"
                onClick={() => navigate(`/orders/${saleOrder.id}`)}
                data-testid="admin-pdp-goto-sale"
              >
                Zur Bestellung
              </Button>
            )}
          </div>
        </div>
      )}


      <Modal
        title={watch?.status === 'RESERVED' ? 'Reservierung anpassen' : 'Uhr reservieren'}
        open={reserveOpen}
        onCancel={() => setReserveOpen(false)}
        onOk={async () => {
          setReserving(true);
          try {
            await apiClient.post(`/api/admin/watches/${id}/reserve`, reserveDate
              ? { reservedUntil: reserveDate.toISOString() }
              : { indefinite: true });
            message.success('Uhr reserviert');
            setReserveOpen(false);
            fetchWatch();
          } catch (e: any) {
            message.error(e.response?.data?.message || 'Fehler');
          } finally {
            setReserving(false);
          }
        }}
        confirmLoading={reserving}
        okText="Reservieren"
        cancelText="Abbrechen"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <span>Reservieren bis (Datum & Uhrzeit, leer = ohne Ablauffrist):</span>
          {isMobile ? (
            // The antd date/time panel is wider than a phone screen; the native picker fits and is touch friendly
            <Space.Compact style={{ width: '100%' }}>
              <Input
                type="datetime-local"
                value={reserveDate ? reserveDate.format('YYYY-MM-DDTHH:mm') : ''}
                min={dayjs().format('YYYY-MM-DDT00:00')}
                onChange={(e) => setReserveDate(e.target.value ? dayjs(e.target.value) : null)}
                data-testid="admin-pdp-reserve-datepicker"
              />
              <Button onClick={() => setReserveDate(null)} disabled={!reserveDate}>Leeren</Button>
            </Space.Compact>
          ) : (
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="DD.MM.YYYY HH:mm"
              value={reserveDate}
              onChange={(d) => setReserveDate(d)}
              disabledDate={(current) => !!current && current.isBefore(dayjs(), 'day')}
              style={{ width: '100%' }}
              data-testid="admin-pdp-reserve-datepicker"
            />
          )}
          <p style={{ color: '#888', fontSize: 12 }}>
            Nach Ablauf wird die Uhr automatisch wieder als „Verfügbar" gelistet. Ohne Datum bleibt sie reserviert, bis die Reservierung manuell aufgehoben wird. Auf der Webseite wird kein Datum angezeigt – nur der Status „Reserviert".
          </p>
        </Space>
      </Modal>

      <Modal
        title="Uhr als verkauft markieren"
        open={soldOpen}
        onCancel={() => setSoldOpen(false)}
        onOk={async () => {
          setMarkingSold(true);
          try {
            await apiClient.post(`/api/admin/watches/${id}/mark-sold`, {
              soldAt: soldDate.toISOString(),
            });
            message.success('Uhr als verkauft markiert');
            setSoldOpen(false);
            fetchWatch();
          } catch (e: any) {
            message.error(e.response?.data?.message || 'Fehler');
          } finally {
            setMarkingSold(false);
          }
        }}
        confirmLoading={markingSold}
        okText="Als verkauft markieren"
        okButtonProps={{ danger: true }}
        cancelText="Abbrechen"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <span>Verkaufsdatum:</span>
          {isMobile ? (
            <Input
              type="date"
              value={soldDate.format('YYYY-MM-DD')}
              max={dayjs().format('YYYY-MM-DD')}
              onChange={(e) => setSoldDate(e.target.value ? dayjs(`${e.target.value}T${dayjs().format('HH:mm')}`) : dayjs())}
              data-testid="admin-pdp-sold-datepicker"
            />
          ) : (
            <DatePicker
              format="DD.MM.YYYY"
              value={soldDate}
              onChange={(d) => setSoldDate(d || dayjs())}
              disabledDate={(current) => !!current && current.isAfter(dayjs(), 'day')}
              style={{ width: '100%' }}
              data-testid="admin-pdp-sold-datepicker"
            />
          )}
          <p style={{ color: '#888', fontSize: 12 }}>
            Direktverkauf ohne Online-Checkout. Die Uhr wird im Verkaufs-Dashboard mit „Direktverkauf" markiert. Eine bestehende Reservierung wird automatisch aufgehoben.
          </p>
        </Space>
      </Modal>

      <Tabs
        items={tabItems}
        activeKey={activeTab}
        onChange={handleTabChange}
      />
    </div>
  );
};

export default ProductDetailPage;
